import type React from "react";

import { cn } from "../../utils";

/**
 * Visual variant for a bounding-box overlay. Consumers pick a
 * variant per box so the viewer can distinguish "the currently
 * selected citation" from "other citations on the same page" from
 * "every parsed chunk" at a glance.
 *
 * - `selected` — prominent, full-color, used for the actively
 *   selected box in the current viewer.
 * - `related` — secondary, used for other boxes in the same logical
 *   group (e.g. siblings of the selected citation).
 * - `muted` — background, used for bulk displays like "every parsed
 *   chunk on this page" so the selected box still stands out.
 */
export type BoundingBoxVariant = "selected" | "related" | "muted";

interface BoundingBoxOverlayProps {
  positionStyle: React.CSSProperties;
  label?: string | null;
  variant?: BoundingBoxVariant;
  overlayRef?: React.Ref<HTMLDivElement>;
  className?: string;
  onClick?: () => void;
}

const BOX_STYLES: Record<BoundingBoxVariant, string> = {
  selected: "border-2 border-green-500 bg-green-500/10",
  related: "border-2 border-blue-400 bg-blue-400/10",
  muted: "border-2 border-orange-500 bg-orange-500/10",
};

const LABEL_STYLES: Record<BoundingBoxVariant, string> = {
  selected: "bg-green-500 text-white",
  related: "bg-blue-400 text-white",
  muted: "bg-orange-500 text-white",
};

/**
 * Generic positioned rectangle rendered on top of a PDF page.
 */
export function BoundingBoxOverlay({
  positionStyle,
  label,
  variant = "selected",
  overlayRef,
  className,
  onClick,
}: BoundingBoxOverlayProps) {
  const clickable = onClick != null;
  // Muted boxes are used in bulk displays (e.g. every parsed chunk on
  // a page). Rendering a label on each one creates a cascade of
  // overlapping translucent pills that crowds the view. Only selected
  // and related variants show labels.
  const showLabel = label != null && variant !== "muted";
  return (
    <div
      ref={overlayRef}
      className={cn(
        "absolute transition-all duration-200 z-10",
        BOX_STYLES[variant],
        clickable ? "cursor-pointer pointer-events-auto" : "pointer-events-none",
        className,
      )}
      style={positionStyle}
      onClick={onClick}
    >
      {showLabel ? (
        <div
          className={cn(
            "absolute -top-6 left-0 text-xs px-2 py-1 rounded whitespace-nowrap max-w-[300px] truncate",
            LABEL_STYLES[variant],
          )}
        >
          {label}
        </div>
      ) : null}
    </div>
  );
}
